import { useCallback } from "react";
import { useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import apiClient from "services/ApiClient";
import { newHireJourneysPath } from "helpers/url/pages/v1";
import { newHireJourneyKeys } from "features/NewHireJourney/hooks";
import { NewHireJourney } from "models/v2/newHireJourney";
import { v2ApiNewHireJourneyPathById } from "helpers/url/api/v2";
import { pendingNewHireKeys } from "../hooks";

interface UseKickOffOnboardingProps {
  newHireJourneyId: string;
  onClose: () => void;
}

export default function useKickOffOnboarding({ newHireJourneyId, onClose }: UseKickOffOnboardingProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  const { mutate: onSubmit, isPending: isSubmitting } = useMutation({
    mutationFn: (_data: object) => apiClient.put<NewHireJourney>(v2ApiNewHireJourneyPathById(newHireJourneyId), {
      newHireJourney: {
        pending: false,
        actionsStatus: "enabled",
      },
    }),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: newHireJourneyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: newHireJourneyKeys.details() });
      queryClient.invalidateQueries({ queryKey: pendingNewHireKeys.lists() });
      queryClient.invalidateQueries({ queryKey: pendingNewHireKeys.count() });
      toast({
        title: `🎉 Success!`,
      });
      onClose();
      navigate(`${newHireJourneysPath()}?highlight=${newHireJourneyId}`);
    },
    onError: (mutateError) => {
      console.log("Error", mutateError);
      toast({ title: "There was an error, please try again", status: "error" });
      onClose();
    },
  });

  const handleSubmit = useCallback(() => {
    onSubmit({});
  }, [onSubmit]);

  return {
    handleSubmit,
    isSubmitting,
  };
}
